// Page de résultats de la recherche universelle (/recherche?q=…).
// Le champ de l'en-tête reste le seul champ de saisie : cette page affiche
// tous les résultats, rangés par famille, avec un filtre par famille.
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  groupUniversalSearchHits,
  isUniversalSearchReady,
  UNIVERSAL_SEARCH_LIMIT_PER_KIND,
  UNIVERSAL_SEARCH_MIN_CHARS,
  UNIVERSAL_SEARCH_PAGE_LIMIT,
  type UniversalSearchKind,
} from '../../lib/universalSearch';
import { useUniversalSearch } from '../../hooks/queries/useUniversalSearch';
import { ListPageLayout } from '../ui/ListPageLayout';
import { ListEmpty, ListToolbar, Segments, type SegmentOption } from '../ui/ListToolbar';
import { FaMagnifyingGlass } from '../ui/FaIcons';
import { Spinner } from '../ui/Spinner';
import { SEARCH_KIND_ICON, SearchHitLink } from './SearchHitLink';

type Filter = 'all' | UniversalSearchKind;

/** « 50 » affiché « 50+ » : la famille a peut-être d'autres résultats non chargés. */
function countLabel(count: number): string {
  return count >= UNIVERSAL_SEARCH_PAGE_LIMIT ? `${UNIVERSAL_SEARCH_PAGE_LIMIT}+` : String(count);
}

export function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = (searchParams.get('q') ?? '').trim();
  const ready = isUniversalSearchReady(query);
  const { data, isFetching, isError } = useUniversalSearch(query, UNIVERSAL_SEARCH_PAGE_LIMIT);

  const sections = useMemo(
    () => (ready ? groupUniversalSearchHits(data ?? [], UNIVERSAL_SEARCH_PAGE_LIMIT) : []),
    [data, ready],
  );
  const total = sections.reduce((sum, section) => sum + section.hits.length, 0);
  const capped = sections.some((section) => section.hits.length >= UNIVERSAL_SEARCH_PAGE_LIMIT);

  const requested = searchParams.get('type') as Filter | null;
  const filter: Filter = requested && sections.some((section) => section.kind === requested) ? requested : 'all';

  function setFilter(next: Filter) {
    const sp = new URLSearchParams(searchParams);
    if (next === 'all') sp.delete('type');
    else sp.set('type', next);
    setSearchParams(sp, { replace: true });
  }

  const options: SegmentOption<Filter>[] = [
    { value: 'all', label: 'Tout', count: total },
    ...sections.map((section) => ({
      value: section.kind as Filter,
      label: section.label,
      count: section.hits.length,
    })),
  ];
  const shown = filter === 'all' ? sections : sections.filter((section) => section.kind === filter);

  const header = (
    <ListToolbar>
      <p className="m-0 min-w-0 truncate text-sm text-cime-text-muted">
        {!ready ? (
          'Recherche'
        ) : (
          <>
            <b className="font-semibold text-cime-text-primary">
              {capped ? `${total}+` : total} résultat{total > 1 ? 's' : ''}
            </b>{' '}
            pour « {query} »
          </>
        )}
      </p>
      {isFetching ? <Spinner className="h-4 w-4" /> : null}
    </ListToolbar>
  );

  return (
    <ListPageLayout header={header}>
      {!ready ? (
        <ListEmpty icon={<FaMagnifyingGlass />}>
          Saisissez au moins {UNIVERSAL_SEARCH_MIN_CHARS} caractères dans le champ de recherche, en haut de la page.
        </ListEmpty>
      ) : isError ? (
        <ListEmpty icon={<FaMagnifyingGlass />}>La recherche a échoué. Réessayez dans un instant.</ListEmpty>
      ) : total === 0 ? (
        isFetching ? (
          <ListEmpty>Recherche en cours…</ListEmpty>
        ) : (
          <ListEmpty icon={<FaMagnifyingGlass />}>
            Aucun résultat pour « {query} ». Vérifiez l'orthographe ou essayez un autre mot.
          </ListEmpty>
        )
      ) : (
        <>
          <div className="overflow-x-auto border-b border-neutral-100 px-4 py-3">
            <Segments options={options} value={filter} onChange={setFilter} label="Famille de résultats" />
          </div>
          {shown.map((section) => {
            const Icon = SEARCH_KIND_ICON[section.kind];
            const preview = filter === 'all';
            const hits = preview ? section.hits.slice(0, UNIVERSAL_SEARCH_LIMIT_PER_KIND) : section.hits;
            const more = section.hits.length - hits.length;
            return (
              <section key={section.kind} className="border-b border-neutral-100 last:border-b-0">
                <header className="flex items-center gap-2 bg-[#fbfaf7] px-4 py-2.5">
                  <Icon className="h-3.5 w-3.5 text-cime-text-muted" />
                  <h2 className="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">
                    {section.label}
                  </h2>
                  <span className="text-[11px] font-bold tabular-nums text-cime-gold-text">
                    {countLabel(section.hits.length)}
                  </span>
                </header>
                <ul className="m-0 list-none divide-y divide-neutral-100 p-0">
                  {hits.map((hit) => (
                    <li key={`${hit.kind}-${hit.id}`}>
                      <SearchHitLink hit={hit} />
                    </li>
                  ))}
                </ul>
                {more > 0 ? (
                  <button
                    type="button"
                    onClick={() => setFilter(section.kind)}
                    className="w-full border-t border-neutral-100 px-4 py-2.5 text-left text-[13px] font-medium text-cime-primary transition-colors hover:bg-neutral-50"
                  >
                    Voir les {countLabel(section.hits.length)} résultats dans « {section.label} »
                  </button>
                ) : null}
                {!preview && section.hits.length >= UNIVERSAL_SEARCH_PAGE_LIMIT ? (
                  <p className="m-0 border-t border-neutral-100 px-4 py-2.5 text-xs text-cime-text-muted">
                    Seuls les {UNIVERSAL_SEARCH_PAGE_LIMIT} premiers résultats sont affichés. Précisez votre recherche pour en voir moins.
                  </p>
                ) : null}
              </section>
            );
          })}
        </>
      )}
    </ListPageLayout>
  );
}
